import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Download, MonitorDown, Smartphone, Check } from "lucide-react";

export function InstallAppButton() {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onBIP = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => { setInstalled(true); setDeferred(null); };
    window.addEventListener("beforeinstallprompt", onBIP);
    window.addEventListener("appinstalled", onInstalled);
    if (window.matchMedia?.("(display-mode: standalone)").matches) setInstalled(true);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBIP);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed) return null;

  const handleClick = async () => {
    if (deferred) {
      deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice?.outcome === "accepted") setDeferred(null);
    } else {
      setOpen(true);
    }
  };

  return (
    <>
      <button
        data-testid="btn-install-pwa"
        onClick={handleClick}
        className="group relative inline-flex items-center gap-2 rounded-sm border border-amber-500/40 bg-amber-500/10 px-3 h-8 text-xs font-medium text-amber-300 transition-all duration-150 hover:bg-amber-500/20 hover:border-amber-500/70 active:scale-95"
        title="Instalar EMAURAN como app web"
      >
        <span className="relative flex h-4 w-4 items-center justify-center">
          <Download className="h-4 w-4" />
          {deferred && (
            <span className="absolute -right-1 -top-1 h-1.5 w-1.5 animate-ping rounded-full bg-amber-400" />
          )}
        </span>
        <span className="hidden md:inline">Instalar app</span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="install-pwa-dialog">
          <DialogHeader>
            <DialogTitle className="font-head flex items-center gap-2">
              <MonitorDown className="h-5 w-5 text-primary" /> Instalar EMAURAN como app web
            </DialogTitle>
            <DialogDescription>
              Instala EMAURAN en tu equipo o teléfono para abrirlo como una aplicación, a pantalla completa y con acceso directo.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="flex gap-3 rounded-sm border border-border p-3">
              <MonitorDown className="h-5 w-5 shrink-0 text-primary" />
              <div>
                <p className="font-medium">Escritorio (Chrome / Edge)</p>
                <p className="text-muted-foreground text-xs mt-0.5">Abre el menú del navegador y elige <b>“Instalar EMAURAN…”</b>, o usa el ícono de instalar en la barra de direcciones.</p>
              </div>
            </div>
            <div className="flex gap-3 rounded-sm border border-border p-3">
              <Smartphone className="h-5 w-5 shrink-0 text-primary" />
              <div>
                <p className="font-medium">iPhone / iPad (Safari)</p>
                <p className="text-muted-foreground text-xs mt-0.5">Toca <b>Compartir</b> y luego <b>“Agregar a inicio”</b>.</p>
              </div>
            </div>
            <div className="flex gap-3 rounded-sm border border-border p-3">
              <Smartphone className="h-5 w-5 shrink-0 text-primary" />
              <div>
                <p className="font-medium">Android (Chrome)</p>
                <p className="text-muted-foreground text-xs mt-0.5">Menú <b>⋮</b> → <b>“Instalar aplicación”</b>.</p>
              </div>
            </div>
            <p className="flex items-center gap-1.5 text-xs text-emerald-600 dark:text-emerald-400">
              <Check className="h-3.5 w-3.5" /> Funciona en todos tus dispositivos con el mismo inicio de sesión.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
